import React, { useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
    Box,
    Typography,
    Card,
    CardContent,
    Divider,
    Button,
    CircularProgress,
    Paper,
    Stack,
    Container,
    Checkbox,
    FormControlLabel,
} from "@mui/material";

import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import LockIcon from "@mui/icons-material/Lock";
import ShoppingBagOutlinedIcon from "@mui/icons-material/ShoppingBagOutlined";

import Swal from "sweetalert2";
import { Link, useNavigate } from "react-router-dom";

import { AddOrder } from "../utils/order";
import { CLEAR_CART } from "../features/productInCartSlice";

import UserDetailsSection from "./UserDetailsSection";
import ShippingMethodSelector from "./ShippingMethodSelector";
import PaymentMethodSelector from "./PaymentMethodSelector";
import DealChip from "./DealChip";

import {
    getUnitPrice,
    calculateItemTotal,
    calculateCartTotal,
} from "../helpers/cartPriceHelper";

const CheckoutSummary = () => {
    const loggedUser = useSelector((state) => state.user.logedUser);
    const cart = useSelector((state) => state.cart.items);

    const dispatch = useDispatch();
    const navigate = useNavigate();

    const [loading, setLoading] = useState(false);
    const [shippingMethod, setShippingMethod] = useState("");
    const [paymentMethod, setPaymentMethod] = useState("");
    const [acceptedTerms, setAcceptedTerms] = useState(false);
    const [errors, setErrors] = useState({});

    const total = calculateCartTotal(cart);

    const API_BASE = process.env.REACT_APP_API_BASE_URL || "";

    const resolveImgSrc = (img) => {
        if (!img) return null;

        if (img instanceof File) {
            return URL.createObjectURL(img);
        }

        const path = String(img).replace(/\\/g, "/");

        if (path.startsWith("http") || path.startsWith("//")) {
            return path;
        }

        return `${API_BASE}${path}`;
    };

    const getCurrentDate = () => {
        const today = new Date();

        const year = today.getFullYear();
        const month = String(today.getMonth() + 1).padStart(2, "0");
        const day = String(today.getDate()).padStart(2, "0");

        return `${year}-${month}-${day}`;
    };

    const getShippingAddress = () => {
        const address = loggedUser?.userAddresses?.[0];

        if (!address) {
            return "";
        }

        return [
            address.city,
            `${address.street || ""} ${address.houseNumber || ""}`.trim(),
            address.apartment ? `דירה ${address.apartment}` : "",
            address.floor ? `קומה ${address.floor}` : "",
            address.entrance ? `כניסה ${address.entrance}` : "",
            address.notes ? `הערות: ${address.notes}` : "",
        ]
            .filter(Boolean)
            .join(", ");
    };

    const validateCheckout = () => {
        const validationErrors = {};

        if (!shippingMethod) {
            validationErrors.shippingMethod = "יש לבחור שיטת משלוח";
        }

        if (!paymentMethod) {
            validationErrors.paymentMethod = "יש לבחור אמצעי תשלום";
        }

        if (!acceptedTerms) {
            validationErrors.acceptedTerms =
                "יש לאשר את תנאי השימוש ותקנון המשלוחים";
        }

        return validationErrors;
    };

    const handlePay = async () => {
        if (cart.length === 0) {
            await Swal.fire({
                title: "העגלה שלך ריקה",
                text: "יש להוסיף מוצרים לפני ביצוע הזמנה.",
                icon: "warning",
                confirmButtonText: "חזרה למוצרים",
                confirmButtonColor: "#D81B60",
            });

            navigate("/products");
            return;
        }

        const validationErrors = validateCheckout();

        if (Object.keys(validationErrors).length > 0) {
            setErrors(validationErrors);

            await Swal.fire({
                title: "חסרים פרטים",
                text: "יש להשלים את כל הפרטים ולאשר את תנאי השימוש.",
                icon: "warning",
                confirmButtonColor: "#D81B60",
            });

            return;
        }

        setErrors({});

        const data = {
            id: 0,
            userId: loggedUser.id,
            totalCost: total,
            date: getCurrentDate(),
            statusId: 3,
            shippingMethod,
            shippingAddress: getShippingAddress(),
            paymentMethod,

            ordersProducts: cart.map((item) => ({
                id: 0,
                productId: item.productId,
                amount: item.amount,
                productOptionId: item.productOptionId ?? null,
            })),
        };

        try {
            setLoading(true);

            console.log("order data:", data);

            const response = await AddOrder(data);

            if (response.status === 200 || response.status === 201) {
                await Swal.fire({
                    title: "ההזמנה התקבלה בהצלחה",
                    html: `
            <div style="line-height:1.8">
              <div>ניצור איתך קשר בהקדם האפשרי.</div>
              <div>סיכום ההזמנה נשלח אלייך למייל.</div>
              <strong>סה"כ: ₪${total.toFixed(2)}</strong>
            </div>
          `,
                    icon: "success",
                    confirmButtonColor: "#58A76B",
                    confirmButtonText: "סיום",
                    customClass: {
                        popup: "custom-swal",
                    },
                });

                dispatch(CLEAR_CART());
                navigate("/");
            }
        } catch (error) {
            console.error("Error creating order:", error);

            await Swal.fire({
                title: "שגיאה בשליחת ההזמנה",
                text: "לא הצלחנו לשלוח את ההזמנה. נסי שוב בעוד רגע.",
                icon: "error",
                confirmButtonColor: "#D81B60",
            });
        } finally {
            setLoading(false);
        }
    };

    if (!loggedUser) {
        return (
            <Box sx={{ mt: 10, px: 2, textAlign: "center" }}>
                <Typography variant="h5" sx={{ mb: 2 }}>
                    יש להתחבר כדי להמשיך לתשלום
                </Typography>

                <Button
                    variant="contained"
                    onClick={() => navigate("/login")}
                    sx={{
                        backgroundColor: "#D81B60",
                        "&:hover": {
                            backgroundColor: "#ad1457",
                        },
                    }}
                >
                    מעבר להתחברות
                </Button>
            </Box>
        );
    }

    return (
        <Box
            dir="rtl"
            sx={{
                minHeight: "100vh",
                py: { xs: 2, md: 5 },
            }}
        >
            <Container maxWidth="lg">
                <Box sx={{ mb: 3, textAlign: "right" }}>
                    <Typography
                        variant="h4"
                        sx={{
                            fontWeight: 800,
                            mb: 0.5,
                        }}
                    >
                        השלמת הזמנה
                    </Typography>

                    <Typography color="text.secondary">
                        בדקי את פרטי ההזמנה ובחרי אמצעי תשלום
                    </Typography>
                </Box>

                <Box
                    sx={{
                        display: "grid",
                        gridTemplateColumns: {
                            xs: "1fr",
                            md: "360px 1fr",
                        },
                        gap: 4,
                        alignItems: "start",
                        direction: "ltr",
                    }}
                >
                    {/* פרטי לקוח, משלוח ותשלום */}
                    <Stack spacing={3} sx={{ direction: "rtl" }}>
                        <Card
                            sx={{
                                borderRadius: 3,
                                boxShadow: "0 5px 24px rgba(0,0,0,0.06)",
                                border: "2px solid #D81B60",
                            }}
                        >
                            <CardContent sx={{ p: { xs: 2, md: 3 } }}>
                                <Typography
                                    variant="h6"
                                    sx={{
                                        fontWeight: 700,
                                        mb: 2,
                                    }}
                                >
                                    פרטי הלקוח וכתובת למשלוח
                                </Typography>

                                <UserDetailsSection />
                            </CardContent>
                        </Card>

                        <Card
                            sx={{
                                borderRadius: 3,
                                border: "2px solid #D81B60",
                            }}
                        >
                            <CardContent sx={{ p: { xs: 2, md: 3 } }}>
                                <Typography
                                    variant="h6"
                                    sx={{
                                        fontWeight: 700,
                                        mb: 2,
                                    }}
                                >
                                    שיטת משלוח
                                </Typography>

                                <ShippingMethodSelector
                                    value={shippingMethod}
                                    onChange={(newValue) => {
                                        setShippingMethod(newValue);

                                        setErrors((previous) => ({
                                            ...previous,
                                            shippingMethod: "",
                                        }));
                                    }}
                                />

                                {errors.shippingMethod && (
                                    <Typography
                                        variant="body2"
                                        color="error"
                                        sx={{ mt: 1 }}
                                    >
                                        {errors.shippingMethod}
                                    </Typography>
                                )}
                            </CardContent>
                        </Card>

                        <Card
                            sx={{
                                borderRadius: 3,
                                border: "2px solid #D81B60",
                            }}
                        >
                            <CardContent sx={{ p: { xs: 2, md: 3 } }}>
                                <PaymentMethodSelector
                                    value={paymentMethod}
                                    onChange={(newValue) => {
                                        setPaymentMethod(newValue);

                                        setErrors((previous) => ({
                                            ...previous,
                                            paymentMethod: "",
                                        }));
                                    }}
                                    error={errors.paymentMethod}
                                />
                            </CardContent>
                        </Card>
                    </Stack>

                    {/* סיכום ההזמנה */}
                    <Card
                        sx={{
                            direction: "rtl",
                            borderRadius: 3,
                            position: { md: "sticky" },
                            top: { md: 20 },
                            border: "2px solid #D81B60",
                        }}
                    >
                        <CardContent sx={{ p: { xs: 2, md: 3 } }}>
                            <Box
                                sx={{
                                    display: "flex",
                                    alignItems: "center",
                                    gap: 1,
                                    mb: 2,
                                }}
                            >
                                <ShoppingBagOutlinedIcon />

                                <Typography variant="h6" sx={{ fontWeight: 800 }}>
                                    סיכום ההזמנה
                                </Typography>
                            </Box>

                            <Divider sx={{ mb: 2 }} />

                            {/* רשימת המוצרים */}
                            <Box
                                sx={{
                                    maxHeight: cart.length > 7 ? 700 : "none",
                                    overflowY: cart.length > 7 ? "auto" : "visible",
                                    pl: cart.length > 7 ? 1 : 0,

                                    "&::-webkit-scrollbar": {
                                        width: "7px",
                                    },

                                    "&::-webkit-scrollbar-track": {
                                        backgroundColor: "#f1f1f1",
                                        borderRadius: "10px",
                                    },

                                    "&::-webkit-scrollbar-thumb": {
                                        backgroundColor: "#c7c7c7",
                                        borderRadius: "10px",
                                    },

                                    "&::-webkit-scrollbar-thumb:hover": {
                                        backgroundColor: "#a9a9a9",
                                    },
                                }}
                            >
                                {cart.map((item, index) => {
                                    const displayImage =
                                        item.productOption?.image || item.product?.image;

                                    const unitPrice = getUnitPrice(item);
                                    const lineTotal = calculateItemTotal(item);

                                    const itemKey = `${item.productId ?? item.product?.id
                                        }-${item.productOptionId ?? "without-option"}`;

                                    return (
                                        <Box key={itemKey}>
                                            <Box
                                                sx={{
                                                    display: "flex",
                                                    alignItems: "flex-start",
                                                    gap: 1.5,
                                                    py: 2,
                                                }}
                                            >
                                                <Box
                                                    component="img"
                                                    src={resolveImgSrc(displayImage)}
                                                    alt={item.product?.name || "מוצר"}
                                                    sx={{
                                                        width: 68,
                                                        height: 68,
                                                        flexShrink: 0,
                                                        objectFit: "cover",
                                                        borderRadius: 2,
                                                        border: "1px solid #eceff1",
                                                    }}
                                                />

                                                <Box sx={{ flex: 1, minWidth: 0 }}>
                                                    <Typography
                                                        sx={{
                                                            fontWeight: 700,
                                                            fontSize: 14,
                                                            lineHeight: 1.4,
                                                        }}
                                                    >
                                                        {item.product?.name}
                                                    </Typography>

                                                    {item.productOption && (
                                                        <Typography
                                                            variant="body2"
                                                            color="text.secondary"
                                                        >
                                                            בחירה: {item.productOption.value}
                                                        </Typography>
                                                    )}

                                                    <Box sx={{ mt: 0.5 }}>
                                                        <DealChip product={item.product} />
                                                    </Box>

                                                    <Typography
                                                        variant="body2"
                                                        color="text.secondary"
                                                        sx={{ mt: 0.7 }}
                                                    >
                                                        {item.amount} × ₪{unitPrice.toFixed(2)}
                                                    </Typography>
                                                </Box>

                                                <Typography
                                                    sx={{
                                                        fontWeight: 700,
                                                        whiteSpace: "nowrap",
                                                    }}
                                                >
                                                    ₪{lineTotal.toFixed(2)}
                                                </Typography>
                                            </Box>

                                            {index < cart.length - 1 && (
                                                <Divider
                                                    sx={{
                                                        width: "100%",
                                                        borderColor: "#e0e0e0",
                                                    }}
                                                />
                                            )}
                                        </Box>
                                    );
                                })}
                            </Box>

                            <Divider sx={{ my: 2.5 }} />

                            <Box
                                sx={{
                                    display: "flex",
                                    justifyContent: "space-between",
                                    mb: 1,
                                }}
                            >
                                <Typography color="text.secondary">
                                    מספר מוצרים
                                </Typography>

                                <Typography>
                                    {cart.reduce(
                                        (sum, item) => sum + Number(item.amount || 0),
                                        0
                                    )}
                                </Typography>
                            </Box>

                            <Box
                                sx={{
                                    display: "flex",
                                    justifyContent: "space-between",
                                    alignItems: "center",
                                    mt: 2,
                                }}
                            >
                                <Typography variant="h6" sx={{ fontWeight: 800 }}>
                                    סה"כ לתשלום
                                </Typography>

                                <Typography
                                    variant="h5"
                                    sx={{
                                        fontWeight: 900,
                                        color: "#D81B60",
                                    }}
                                >
                                    ₪{total.toFixed(2)}
                                </Typography>
                            </Box>

                            {/* אישור תנאים */}
                            <Box
                                sx={{
                                    mt: 3,
                                    direction: "rtl",
                                }}
                            >
                                <FormControlLabel
                                    control={
                                        <Checkbox
                                            checked={acceptedTerms}
                                            onChange={(event) => {
                                                const checked = event.target.checked;

                                                setAcceptedTerms(checked);

                                                if (checked) {
                                                    setErrors((previous) => ({
                                                        ...previous,
                                                        acceptedTerms: "",
                                                    }));
                                                }
                                            }}
                                            sx={{
                                                color: errors.acceptedTerms
                                                    ? "error.main"
                                                    : "#9e9e9e",

                                                "&.Mui-checked": {
                                                    color: "#D81B60",
                                                },
                                            }}
                                        />
                                    }
                                    label={
                                        <Typography
                                            variant="body2"
                                            sx={{
                                                lineHeight: 1.8,
                                                textAlign: "right",
                                            }}
                                        >
                                            בלחיצה על כפתור זה הריני מאשר/ת את{" "}
                                            <Box
                                                component={Link}
                                                to="/conditions"
                                                onClick={(event) => event.stopPropagation()}
                                                sx={{
                                                    color: "#D81B60",
                                                    fontWeight: 700,
                                                    textDecoration: "underline",
                                                }}
                                            >
                                                תנאי השימוש
                                            </Box>
                                            {" "}ואת{" "}
                                            <Box
                                                component={Link}
                                                to="/shipping-policy"
                                                onClick={(event) => event.stopPropagation()}
                                                sx={{
                                                    color: "#D81B60",
                                                    fontWeight: 700,
                                                    textDecoration: "underline",
                                                }}
                                            >
                                                תקנון המשלוחים
                                            </Box>
                                        </Typography>
                                    }
                                    sx={{
                                        m: 0,
                                        width: "100%",
                                        alignItems: "flex-start",

                                        "& .MuiFormControlLabel-label": {
                                            flex: 1,
                                            pt: 0.6,
                                        },
                                    }}
                                />

                                {errors.acceptedTerms && (
                                    <Typography
                                        variant="body2"
                                        color="error"
                                        sx={{
                                            mt: 0.5,
                                            mr: 4.5,
                                            textAlign: "right",
                                        }}
                                    >
                                        {errors.acceptedTerms}
                                    </Typography>
                                )}
                            </Box>

                            <Button
                                fullWidth
                                variant="contained"
                                size="large"
                                onClick={handlePay}
                                disabled={loading}
                                sx={{
                                    mt: 2,
                                    minHeight: 52,
                                    borderRadius: 2,
                                    fontWeight: 800,
                                    fontSize: 16,
                                    backgroundColor: "#D81B60",

                                    "&:hover": {
                                        backgroundColor: "#ad1457",
                                    },
                                }}
                            >
                                {loading ? (
                                    <>
                                        <CircularProgress
                                            size={22}
                                            sx={{
                                                color: "#fff",
                                                ml: 1,
                                            }}
                                        />
                                        שולח הזמנה...
                                    </>
                                ) : (
                                    "אישור ושליחת ההזמנה"
                                )}
                            </Button>

                            <Button
                                fullWidth
                                variant="text"
                                startIcon={<ArrowBackIcon />}
                                onClick={() => navigate("/cart")}
                                disabled={loading}
                                sx={{
                                    mt: 1,
                                    color: "#455a64",
                                }}
                            >
                                חזרה לעגלת הקניות
                            </Button>

                            <Paper
                                elevation={0}
                                sx={{
                                    mt: 2,
                                    p: 1.5,
                                    borderRadius: 2,
                                    backgroundColor: "#f6f8fa",
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "center",
                                    gap: 1,
                                }}
                            >
                                <LockIcon
                                    sx={{
                                        fontSize: 18,
                                        color: "success.main",
                                    }}
                                />

                                <Typography
                                    variant="body2"
                                    color="text.secondary"
                                    sx={{
                                        textAlign: "center",
                                    }}
                                >
                                    ההזמנה והפרטים שלך נשמרים בצורה מאובטחת
                                </Typography>
                            </Paper>
                        </CardContent>
                    </Card>
                </Box>
            </Container>
        </Box>
    );
};

export default CheckoutSummary;